import { useEffect,useState } from "react";
import { useTranslation } from "react-i18next";

import WPAccordion from "../../../packages/WPAccordion";

import DupontCheckbox from "../../DupontCheckbox";
import TooltipRenderer from "../../TooltipRenderer";

import { MAX_TRAINS_FOR_BENCHMARK } from "../../../constants/global.constants";

import "./PlantTrainsAccordion.scss";

const PlantTrainsAccordion = ({
  plants = [],
  benchmarkPlantTrains,
  setBenchmarkPlantTrains,
  selectedTrainsCount,
}) => {
  const { t } = useTranslation();
  const [showMaxAlert, setShowMaxAlert] = useState(false);

  const handleTrainChange = (trainId, plantId, isChecked) => {
    setBenchmarkPlantTrains((prev) => {
      const updatedState = { ...prev };
      if (!updatedState[plantId]) {
        updatedState[plantId] = [];
      }

      if (isChecked) {
        updatedState[plantId] = [...updatedState[plantId], trainId];
      } else {
        updatedState[plantId] = updatedState[plantId].filter(
          (id) => id !== trainId,
        );
        if (updatedState[plantId].length === 0) {
          delete updatedState[plantId];
        }
      }

      return updatedState;
    });
  };

  const handleSelectAllTrains = (plantId, isChecked) => {
    const allTrains = plants.find((p) => p.plant_id === plantId)?.trains || [];

    setBenchmarkPlantTrains((prev) => {
      const updatedState = { ...prev };
      if (!updatedState[plantId]) {
        updatedState[plantId] = [];
      }

      if (isChecked) {
        updatedState[plantId] = allTrains.map((train) => train.train_id);
      } else {
        delete updatedState[plantId];
      }

      return updatedState;
    });
  };

  const handleMaxCheck = (e) => {
    if (selectedTrainsCount === MAX_TRAINS_FOR_BENCHMARK && e.target.checked) {
      setShowMaxAlert(true);
    }
  };

  useEffect(() => {
    if (!showMaxAlert) return;
    const timeoutId = setTimeout(() => setShowMaxAlert(false), 3000);
    return () => clearTimeout(timeoutId);
  }, [showMaxAlert]);

  return (
    <div className="selected-plants-container">
      {plants.map((plant, index) => {
        const { plant_id, plant_name, trains } = plant;
        const selectedTrains = benchmarkPlantTrains[plant_id] || [];
        const allTrains = trains || [];
        const selectAllChecked =
          selectedTrains.length === allTrains.length && allTrains.length > 0;

        return (
          <div key={plant_id} className="plant-accordion-wrapper">
            <WPAccordion
              title={<TooltipRenderer title={plant_name} />}
              defaultOpen={index === 0}
              sideInfo={`${selectedTrains.length}/${allTrains.length}`}
            >
              <div className="accordion-content">
                <div className="select-all-wrapper">
                  <DupontCheckbox
                    key={`select-all-checkbox-${plant_id}`}
                    id={`select-all-${plant_id}`}
                    labelText={t("benchMark.selectAllTrainsLabel")}
                    checked={selectAllChecked}
                    onChange={(isChecked) =>
                      handleSelectAllTrains(plant_id, isChecked)
                    }
                    allowAction={selectedTrainsCount < MAX_TRAINS_FOR_BENCHMARK}
                    onClick={handleMaxCheck}
                    indeterminate={
                      selectedTrains.length > 0 && !selectAllChecked
                    }
                  />
                </div>

                <div className="trains-list">
                  {allTrains.map((train) => {
                    const { train_id } = train;
                    return (
                      <div key={train_id} className="train-item">
                        <DupontCheckbox
                          key={`train-checkbox-${plant_id}-${train_id}`}
                          id={`train-${plant_id}-${train_id}`}
                          labelText={
                            <TooltipRenderer
                              title={`${t("ValueStrings.train")} ${train_id}`}
                            />
                          }
                          checked={selectedTrains.includes(train_id)}
                          onChange={(isChecked) =>
                            handleTrainChange(train_id, plant_id, isChecked)
                          }
                          allowAction={
                            selectedTrainsCount < MAX_TRAINS_FOR_BENCHMARK
                          }
                          onClick={handleMaxCheck}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            </WPAccordion>
          </div>
        );
      })}

      {showMaxAlert && (
        <div className="max-trains-alert">
          <div className="alert-content">{t("benchMark.maxTrainsAlert")}</div>
        </div>
      )}
    </div>
  );
};

export default PlantTrainsAccordion;
